import { Button, Card } from "react-bootstrap";

import { FileText, ExternalLink, FileTextIcon } from "lucide-react";
import { DateTime } from "luxon";
import { twMerge } from "tailwind-merge";

import { getDateFromIso } from "@/_helpers/stringHelper";
import { useGetUserType } from "@/modules/auth/hooks/userType.hooks";
import { getReminderDaysInterval } from "@/modules/order/components/OrderInvoiceInfosPreview/utils/invoice-reminder.utils";

import { CustomButtonLink } from "./CustomButtonLink";
import { ReminderBox } from "./ReminderBox";
import { StatusTag } from "./StatusTag";

interface OrderInvoicesProps {
  customerInvoice?: any;
  estimateData: any[];
}

const OrderInvoices: React.FC<OrderInvoicesProps> = ({
  customerInvoice,
  estimateData,
}) => {
  const { isSuperAdmin } = useGetUserType();

  const getInvoice = () => {
    if (customerInvoice) {
      return customerInvoice;
    }

    if (
      estimateData.length > 0 &&
      estimateData[0].related_invoices?.length > 0
    ) {
      return estimateData[0].related_invoices[0];
    }

    return null;
  };

  if (estimateData.length === 0 && !customerInvoice) {
    return null;
  }
  const invoice = getInvoice();
  const amountBeforeTax = invoice?.currency_amount_before_tax;
  const invoiceDeadlineDate = invoice?.deadline;
  const isDraftInvoice = invoice?.is_draft === true;
  const isInvoiceFullyPaid = invoice?.fully_paid_at;
  const canInvoiceBePaid = !isDraftInvoice && !isInvoiceFullyPaid;
  const invoiceV2Id = invoice?.v2_id;
  const reminderDaysInterval = getReminderDaysInterval({
    endDate: invoiceDeadlineDate,
  });
  const paymentNotLateWithoutReminder = reminderDaysInterval < -2;
  const getReminderPeriod = () => {
    if (!canInvoiceBePaid || isInvoiceFullyPaid) {
      return undefined;
    }

    // Only display reminders if the deadline interval is above -2 days
    if (reminderDaysInterval <= -2) {
      return undefined;
    }

    const latestPaymentDate =
      invoice?.payments?.length > 0 &&
      Number(invoice?.payments[0]?.currency_amount) < Number(invoice?.amount)
        ? invoice?.payments[0]?.created_at
        : undefined;

    const latestPaymentInterval = getReminderDaysInterval({
      startDate: latestPaymentDate,
      endDate: invoiceDeadlineDate,
    });

    // Show reminders at specific day intervals
    if (reminderDaysInterval >= -1 && reminderDaysInterval <= 0) {
      return <ReminderBox text="J-2" />;
    } else if (reminderDaysInterval >= 1 && reminderDaysInterval <= 6) {
      return (
        <ReminderBox className="bg-red-FF2929/10 text-red-FF2929" text="J+1" />
      );
    } else if (reminderDaysInterval >= 7 && reminderDaysInterval <= 13) {
      return (
        <ReminderBox className="bg-red-FF2929/10 text-red-FF2929" text="J+7" />
      );
    } else if (reminderDaysInterval === 14) {
      return (
        <ReminderBox className="bg-red-FF2929/10 text-red-FF2929" text="J+14" />
      );
    } else if (latestPaymentInterval > 0) {
      return (
        <ReminderBox
          className="bg-red-FF2929/10 text-red-FF2929"
          text={`Facture en retard de paiement depuis J+${latestPaymentInterval}`}
        />
      );
    } else {
      // Show remaining late days for other time periods
      return (
        <ReminderBox
          className="bg-red-FF2929/10 text-red-FF2929"
          text={`Facture non payée depuis J+${reminderDaysInterval}`}
        />
      );
    }
  };

  const customerHasBeenReminded = !!getReminderPeriod();

  // Helper function to build Pennylane URLs
  const buildPennylaneUrl = (type: "estimate" | "invoice", id: number) => {
    const isFromServiceData =
      !customerInvoice?.data?.invoice &&
      estimateData?.[0]?.related_invoices?.length > 0;
    const companyId = isFromServiceData
      ? import.meta.env.VITE_PENNYLANE_LEGIMETRIE_COMPANY_ID
      : import.meta.env.VITE_PENNYLANE_PHOENIX_COMPANY_ID;

    const currentYear = new Date().getFullYear();
    const startDate = `${currentYear - 1}-06-30`;
    const endDate = `${currentYear}-06-30`;

    const filter = encodeURIComponent(
      `[{"field":"date","operator":"gteq","value":"${startDate}"},{"field":"date","operator":"lteq","value":"${endDate}"}]`
    );

    if (type === "estimate") {
      return `https://app.pennylane.com/companies/${companyId}/clients/customer_estimates?estimate_id=${id}&filter=${filter}&page=1&sort=`;
    } else {
      return `https://app.pennylane.com/companies/${companyId}/clients/customer_invoices?filter=${filter}&invoice_id=${id}&page=1&sort=-date%2C%2Bid&subtab=all`;
    }
  };

  return (
    <div className="max-w-sm mx-auto">
      <Card className="border-0 shadow-md bg-white">
        <Card.Header>
          <Card.Title className="flex items-center space-x-2">
            <FileTextIcon className="w-4 h-4 text-purple-600" />
            <span className="text-md">Détails de la facture</span>
          </Card.Title>
        </Card.Header>
        <Card.Body>
          {/* Invoice Details */}
          <div className="bg-gray-50 rounded-lg p-3 mb-2 space-y-1">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
              <div>
                <label className="text-sm font-medium text-gray-600">
                  Numéro de facture
                </label>
                <p className="text-md font-bold text-gray-900 mt-1">
                  {invoice?.invoice_number || invoice?.id}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">
                  Montant
                </label>
                <p className="text-md font-bold text-gray-900 mt-1">
                  {amountBeforeTax
                    ? `${Number(amountBeforeTax).toFixed(2).toString().replace(".", ",")} € HT`
                    : ""}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">
                  Date d'émission
                </label>
                <p className="text-md font-medium text-gray-900 mt-1">
                  {getDateFromIso(invoice?.date, DateTime.DATE_SHORT)}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">
                  Date d'échéance
                </label>
                <p className="text-md font-medium text-gray-900 mt-1">
                  {isInvoiceFullyPaid
                    ? getDateFromIso(
                        invoice?.fully_paid_at,
                        DateTime.DATE_SHORT
                      )
                    : paymentNotLateWithoutReminder
                      ? getDateFromIso(invoiceDeadlineDate, DateTime.DATE_SHORT)
                      : "En attente"}
                </p>
              </div>
            </div>

            <div className="flex flex-col items-start pt-2">
              <label className="text-sm font-medium text-gray-600">
                Statut
              </label>
              <div className="mt-1">
                <StatusTag
                  className={twMerge(
                    "font-semibold px-3 py-1",
                    (() => {
                      if (isDraftInvoice) {
                        return "bg-orange-100 text-orange-700";
                      } else if (isInvoiceFullyPaid) {
                        return "bg-green-100 text-green-700";
                      } else {
                        return "bg-red-100 text-red-700"; // Awaiting payment
                      }
                    })()
                  )}
                  text={(() => {
                    if (isDraftInvoice) {
                      return "non finalisée";
                    } else if (isInvoiceFullyPaid) {
                      return "Payée";
                    } else {
                      return "En attente de paiement";
                    }
                  })()}
                />
              </div>
            </div>

            {/* Reminder Box */}
            {customerHasBeenReminded && (
              <div className="pt-1">
                <label className="text-sm font-medium text-gray-600">
                  Dernière relance
                </label>
                <div className="mt-1">{getReminderPeriod()}</div>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="space-y-1">
            <Button
              variant="outline"
              className="w-full bg-purple-600 hover:bg-purple-700 text-white py-1 px-2 rounded-lg flex items-center justify-center gap-1 transition-colors duration-200"
              onClick={() => window.open(invoice?.file_url, "_blank")}
            >
              <FileText className="w-4 h-4" />
              Télécharger la facture en PDF
            </Button>

            {canInvoiceBePaid && (
              <CustomButtonLink
                className="w-full bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded-lg flex items-center justify-center gap-2 transition-colors duration-20"
                to={invoice?.public_url}
                text="Payer la facture"
                iconClassName="pi pi-credit-card"
              />
            )}

            {isSuperAdmin && (
              <Button
                variant="outline"
                className="w-full border border-purple-200 text-purple-600 hover:bg-purple-50 py-1 px-2 rounded-lg flex items-center justify-center gap-1 transition-colors duration-200"
                onClick={() =>
                  window.open(
                    buildPennylaneUrl("invoice", invoiceV2Id),
                    "_blank"
                  )
                }
              >
                <ExternalLink className="w-4 h-4" />
                Ouvrir dans Pennylane
              </Button>
            )}
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

export default OrderInvoices;
